import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MessageCircle, ShieldAlert } from "lucide-react";
import { CONTACT_LINKS, HANTAVIRUS } from "@/lib/site-data";

/**
 * Compact landing-page band that surfaces the Hantavirus awareness page and
 * routes anyone with symptoms straight to a doctor on WhatsApp.
 */
export default function HantavirusAlert() {
  return (
    <section
      id="hantavirus-alert"
      data-testid="hantavirus-alert"
      aria-label="Hantavirus awareness"
      className="relative overflow-hidden bg-ink text-cream"
    >
      <div
        aria-hidden="true"
        className="absolute -top-32 -right-24 h-[380px] w-[380px] rounded-full bg-clay/25 blur-3xl"
      />
      <div
        aria-hidden="true"
        className="absolute -bottom-32 -left-24 h-[320px] w-[320px] rounded-full bg-forest/40 blur-3xl"
      />

      <div className="container-x relative py-16 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="inline-flex items-center gap-2 rounded-full bg-clay/15 px-3.5 py-1.5 text-[12.5px] font-semibold uppercase tracking-[0.18em] text-clay">
              <ShieldAlert className="h-3.5 w-3.5" />
              {HANTAVIRUS.hero.eyebrow}
            </p>
            <h2 className="mt-4 font-display text-[28px] sm:text-[36px] lg:text-[44px] font-extrabold leading-[1.05] tracking-tight text-balance">
              Hantavirus is rare — but deadly when missed.{" "}
              <span className="text-clay">Know the signs.</span>
            </h2>
            <p className="mt-4 text-[15.5px] sm:text-[16.5px] text-cream/85 leading-relaxed max-w-2xl">
              Fever, severe muscle aches and shortness of breath after rodent exposure?
              Don't wait. Message a licensed Nigerian doctor on WhatsApp — 24/7 triage,
              no app, no waiting room.
            </p>
          </div>

          <div className="lg:col-span-5">
            <div className="flex flex-col gap-3">
              <a
                href={HANTAVIRUS.cta.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hanta-alert-whatsapp"
                className="group inline-flex items-center justify-between rounded-2xl bg-clay text-cream px-6 py-4 hover:-translate-y-0.5 transition-transform shadow-lg"
              >
                <span className="flex items-center gap-3">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-cream text-clay">
                    <MessageCircle className="h-5 w-5" />
                  </span>
                  <span className="text-left">
                    <span className="block text-[11.5px] uppercase tracking-[0.18em] text-cream/85">
                      Talk to a doctor
                    </span>
                    <span className="block font-display text-[16.5px] font-bold leading-tight">
                      {CONTACT_LINKS.whatsapp.label}
                    </span>
                  </span>
                </span>
                <ArrowRight className="h-5 w-5 text-cream/90 transition-transform group-hover:translate-x-1" />
              </a>
              <Link
                to="/hantavirus"
                data-testid="hanta-alert-learn"
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-cream/30 px-6 py-3.5 text-[14.5px] font-semibold text-cream hover:bg-cream/10 transition-colors"
              >
                Learn the signs & how it spreads
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
